import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

import { cn } from '../../lib/utils';
// Leaf import rather than the `shared/hooks` barrel — a UI primitive must not
// pull that whole graph in.
import { useSafeCollisionPadding } from '../../shared/hooks/useSafeAreaInsets';
import { containerDirection, documentDirection } from '../../shared/utils/bidi';

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, collisionPadding, ...props }, ref) => {
  // Resolved from the children when they are readable as text, and omitted
  // otherwise so the portalled content inherits the document direction.
  //
  // The `dir="auto"` this carried until WI cf3fc9a4 was aimed at a real problem
  // (a tooltip holding a number or a code) with the wrong instrument: a
  // digit+separator run needs isolating with `<LtrToken>` (STYLE_GUIDE
  // § Bidirectional Text, Class B), not a base-direction flip of the whole
  // tooltip — and the flip fired on any Hebrew tooltip merely beginning with a
  // Latin word. Word majority answers both a translated sentence and a bare
  // entity name, so resolving is strictly better than either the old blanket
  // `auto` or a blanket inherit. `props.dir` still overrides.
  // Leaf import, not the direction context — see the note in `label.tsx`.
  const contentDir = containerDirection(props.children, documentDirection());

  // The tooltip is positioned against the VIEWPORT, which on the edge-to-edge
  // Android shell runs behind the navigation bar — so a tooltip anchored to a
  // control low on the screen can settle underneath it and be unreadable.
  // `collisionPadding` is a JS number, so it cannot read `var(--safe-bottom)`;
  // the hook resolves the inset to pixels and keeps a caller's own padding as a
  // floor. Every inset is 0px on the web and on desktop, so nothing moves there.
  const safeCollisionPadding = useSafeCollisionPadding(collisionPadding);

  return (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      dir={contentDir}
      sideOffset={sideOffset}
      collisionPadding={safeCollisionPadding}
      className={cn(
        'z-50 overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md whitespace-pre-line animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        className
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
  );
});
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
